import React, { useState } from 'react'
import { Form } from 'formik'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Button } from '@/components/button'
import { FieldDropdown } from '@/components/form'
import { CreditCardInput } from '@/features/Payments/forms'
import { StateDropdown } from '@/components/address-form'
import useBasic from '@/hooks/use-basic'
import useMediaQuery from '@/hooks/use-media-query'
import { getFormattedCountries } from '@/utils/address'
import { Row, Group, FieldInput } from '@/components/form'
import { breakpoints, colors } from '@/styles'
import { PaymentMethod } from '@/pages/Settings/PlanSettings/use-payment-methods'
import { Label } from '@/components/text'
import useTaxCode from '@/hooks/use-tax-code'
import { AdditionalModalProps } from '@/components/modalize/mui'
import { FormValues } from './types'
import { showDeletePaymentModal } from '../delete-payment-modal'

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: 1;
  button:nth-child(2) {
    margin-left: 20px;
  }
`

const TextButton = styled.button`
  background: none;
  border: none;
  color: ${colors.link};
  cursor: pointer;
`

type FormProps = Pick<AdditionalModalProps, 'ModalFooter'> & {
  values: FormValues
  isSubmitting: boolean
  isValid: boolean
  setFieldValue: (field: string, value: any) => void
  close: () => void
  card?: PaymentMethod
  taxFlag?: boolean
  onSubmit?: () => void
}

const BillingInfoForm: React.FC<FormProps> = ({
  ModalFooter,
  values,
  isValid,
  isSubmitting,
  setFieldValue,
  close,
  taxFlag,
  onSubmit,
  card
}) => {
  const { t } = useTranslation()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const colSize = displayMobile ? 12 : 6
  const { countries } = useBasic(['countries'])
  const formattedCountries = getFormattedCountries(countries)
  const paymentMethodId = card?.payment_method_id
  const hasPaymentMethod = !!paymentMethodId

  const saveButtonText = hasPaymentMethod
    ? t('l.plan_settings.edit.save_changes.button', 'Save Changes')
    : t('common.save', 'Save')

  const [cardInfoValid, setCardInfoValid] = useState(false)
  const [cardMethodValid, setCardMethodValid] = useState(false)

  const [open, setOpen] = useState(!!values.tax_code)
  const { button, label } = useTaxCode(values.country, values.state)
  const showTaxCode = !!label

  const onDelete = async () => {
    showDeletePaymentModal({ paymentMethodId })
    close()
  }

  const handleStateChange = (state: string) => {
    if (state !== values.state) {
      setOpen(false)
      setFieldValue('tax_code', '')
    }
  }

  return (
    <StyledForm>
      <Row>
        <Group col={colSize}>
          <FieldInput
            id="first_name"
            name="first_name"
            autoComplete={' billing cc-given-name'}
            placeholder={t('l.plan_settings.edit.first_name.label', 'First Name')}
            required
          />
        </Group>
        <Group col={colSize}>
          <FieldInput
            id="last_name"
            name="last_name"
            autoComplete={'billing cc-family-name'}
            placeholder={t('l.plan_settings.edit.last_name.label', 'Last Name')}
            required
          />
        </Group>
      </Row>
      <CreditCardInput
        autocompletePrefix="billing"
        setCardInfoValid={setCardInfoValid}
        setCardMethodValid={setCardMethodValid}
        placeholder={card?.card_number}
        cardMethods={{
          acceptedCards: ['unionpay', 'visa', 'mastercard', 'american-express'],
          error: t(
            'r.payment.edit.unknown_card.error',
            '** The credit card you have entered is not accepted. please try again with a different card.'
          )
        }}
      />
      <Row top={16}>
        <Group col={colSize}>
          <FieldInput
            name="street1"
            autoComplete={'billing address-line2'}
            placeholder={t('l.plan_settings.edit.street_address.label', 'Street address')}
            required
          />
        </Group>
        <Group col={colSize}>
          <FieldInput
            name="street2"
            autoComplete={'billing address-line1'}
            placeholder={t('l.plan_settings.edit.street_address_2.label', 'Suite no. / Unit no.')}
          />
        </Group>
      </Row>
      <Row>
        <Group col={colSize}>
          <FieldInput
            name="city"
            autoComplete={'billing address-level2'}
            placeholder={t('l.plan_settings.edit.city.label', 'City')}
            required
          />
        </Group>
        <Group col={colSize}>
          <StateDropdown
            name="state"
            country={values.country}
            isSearchable={false}
            required
            menuPlacement={displayMobile ? 'top' : undefined}
            autoComplete={'billing address-level1'}
            onChange={handleStateChange}
          />
        </Group>
      </Row>
      <Row>
        <Group col={colSize}>
          <FieldDropdown
            id="country"
            name="country"
            options={formattedCountries}
            autoComplete="billing country"
            onChange={() => setFieldValue('state', '')}
            placeholder={t('r.settings.edit.country.label', 'Country')}
            required
            isSearchable={false}
            isDisabled
          />
        </Group>
        <Group col={colSize}>
          <FieldInput
            name="zip"
            autoComplete="billing postal-code"
            placeholder={t('r.payment.edit.make_payment_zip.label', 'Postal code')}
            required
          />
        </Group>
      </Row>
      {taxFlag &&
        showTaxCode &&
        (!open ? (
          <Row top="-4">
            <Group col="12">
              <TextButton type="button" onClick={() => setOpen(true)}>
                <Label fontSize="12px" as="span">
                  {button}
                </Label>
              </TextButton>
            </Group>
          </Row>
        ) : (
          <Row>
            <Group col="12">
              <FieldInput
                name="tax_code"
                autoComplete={`billing one-time-code`}
                placeholder={label}
              />
            </Group>
          </Row>
        ))}
      <ModalFooter>
        <ButtonsWrapper>
          {hasPaymentMethod && (
            <Button type="button" theme="delete" onClick={onDelete}>
              {t('common.delete', 'Delete')}
            </Button>
          )}
          <ButtonGroup>
            {!displayMobile && (
              <Button type="button" theme="secondary" onClick={close}>
                {t('common.cancel', 'Cancel')}
              </Button>
            )}
            <Button
              type="button"
              theme="primary"
              onClick={onSubmit}
              disabled={isSubmitting || !cardInfoValid || !cardMethodValid || !isValid}
              width={displayMobile && !hasPaymentMethod ? '100%' : 'auto'}
            >
              {saveButtonText}
            </Button>
          </ButtonGroup>
        </ButtonsWrapper>
      </ModalFooter>
    </StyledForm>
  )
}

export default BillingInfoForm
